import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import {
  Bot, Zap, Sparkles, Package, Users, Shield, Check, X,
  Instagram, Twitter, Youtube, Github, MessageSquare, ChevronRight, Loader2,
} from "lucide-react";
import "./landing.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICON_MAP = { Bot, Zap, Sparkles, Package, Users, Shield };

// Pick the right text from a {es: "...", en: "..."} bundle, falling back gracefully.
function pickLang(node, lang) {
  if (node === null || node === undefined) return "";
  if (typeof node === "string") return node;
  if (typeof node === "object") {
    return node[lang] || node.es || node.en || Object.values(node)[0] || "";
  }
  return String(node);
}

function applyTheme(theme) {
  if (!theme) return;
  const root = document.documentElement;
  if (theme.background) root.style.setProperty("--landing-bg", theme.background);
  if (theme.surface) root.style.setProperty("--landing-surface", theme.surface);
  if (theme.border) root.style.setProperty("--landing-border", theme.border);
  if (theme.primary) root.style.setProperty("--landing-primary", theme.primary);
  if (theme.accent) root.style.setProperty("--landing-accent", theme.accent);
  if (theme.text) root.style.setProperty("--landing-text", theme.text);
  if (theme.text_muted) root.style.setProperty("--landing-text-muted", theme.text_muted);
  if (theme.code_font) root.style.setProperty("--landing-mono", theme.code_font);
}

export default function LandingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lang, setLang] = useState(localStorage.getItem("botito_lang") || "es");
  const [billing, setBilling] = useState("monthly");
  const [planModal, setPlanModal] = useState(null);   // selected plan object
  const [planForm, setPlanForm] = useState({ name: "", email: "", phone: "", payment_method: "Transferencia bancaria", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const openPlanModal = (plan) => {
    // Logged-in user → straight to in-app checkout
    if (user) {
      navigate(`/dashboard/plans?plan=${encodeURIComponent(plan.slug)}`);
      return;
    }
    setSubmitted(false); setSubmitError("");
    setPlanForm({ name: "", email: "", phone: "", payment_method: "Transferencia bancaria", notes: "" });
    setPlanModal(plan);
  };

  const submitPlan = async () => {
    if (!planForm.name || !planForm.email) { setSubmitError("Nombre y email son obligatorios"); return; }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(planForm.email)) { setSubmitError("Email inválido"); return; }
    setSubmitting(true); setSubmitError("");
    try {
      const b = planModal.billing?.[billing] || {};
      await axios.post(`${API}/subscriptions/request`, {
        ...planForm,
        plan_slug: planModal.slug,
        plan_name: pickLang(planModal.name, lang),
        billing_period: billing,
        price: b.price || 0,
        currency: b.currency || "USD",
      });
      setSubmitted(true);
    } catch (e) {
      setSubmitError(e.response?.data?.detail || e.message);
    } finally { setSubmitting(false); }
  };

  useEffect(() => {
    axios.get(`${API}/landing/public`)
      .then(({ data }) => {
        setData(data);
        applyTheme(data.settings?.theme);
        const def = data.settings?.language_default;
        if (def && !localStorage.getItem("botito_lang")) {
          setLang(def);
        }
      })
      .catch((e) => console.error("landing load failed", e))
      .finally(() => setLoading(false));
  }, []);

  const settings = data?.settings || {};
  const plans = data?.plans || [];
  const enabled = settings.sections_enabled || {};
  const order = settings.section_order || [
    "hero", "trust", "features", "demo", "how_it_works",
    "plans", "testimonials", "faq", "contact", "footer",
  ];

  const onSwitchLang = (l) => {
    setLang(l);
    localStorage.setItem("botito_lang", l);
  };

  const T = useMemo(() => (n) => pickLang(n, lang), [lang]);

  if (loading) {
    return (
      <div className="landing-root flex items-center justify-center min-h-screen">
        <div className="font-mono text-sm" style={{ color: "var(--landing-primary)" }}>
          <span className="cursor-blink">loading bo-tito</span>
        </div>
      </div>
    );
  }

  const hero = settings.hero || {};
  const trust = settings.trust || {};
  const features = settings.features || {};
  const demo = settings.demo || {};
  const how = settings.how_it_works || {};
  const plansSection = settings.plans_section || {};
  const testimonials = settings.testimonials || {};
  const faq = settings.faq || {};
  const contact = settings.contact || {};
  const footer = settings.footer || {};

  // --- Section renderers ---
  const renderHero = () => enabled.hero !== false && (
    <section className="section pt-24 pb-12" data-testid="landing-hero">
      <div className="grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <div className="section-eyebrow">{T(hero.eyebrow)}</div>
          <h1 className="section-title font-mono whitespace-pre-line glow-text" style={{ fontSize: "clamp(36px, 5vw, 64px)" }}>
            {T(hero.title)}
          </h1>
          <p className="section-subtitle mt-5 text-lg">{T(hero.subtitle)}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <button onClick={() => navigate("/register")} className="btn-primary" data-testid="hero-cta-primary">
              {T(hero.cta_primary)} <ChevronRight className="w-4 h-4" />
            </button>
            <button onClick={() => navigate("/login")} className="btn-ghost" data-testid="hero-cta-secondary">
              {T(hero.cta_secondary)}
            </button>
          </div>
        </div>
        {hero.show_terminal_demo !== false && (
          <div className="terminal-card" data-testid="hero-terminal">
            <div className="terminal-bar">
              <span className="terminal-dot" style={{ background: "#ff5f56" }}></span>
              <span className="terminal-dot" style={{ background: "#ffbd2e" }}></span>
              <span className="terminal-dot" style={{ background: "#27c93f" }}></span>
              <span className="ml-3 font-mono text-xs" style={{ color: "var(--landing-text-muted)" }}>
                bo-tito @ workspace · live
              </span>
            </div>
            <div className="font-mono text-sm p-5 leading-relaxed">
              {(hero.terminal_lines || []).map((line, i) => {
                const isOk = line.startsWith("✓");
                const isCmd = line.startsWith("$");
                const isArrow = line.startsWith("→");
                return (
                  <div key={i} style={{ color: isOk ? "var(--landing-primary)" : isArrow ? "var(--landing-accent)" : isCmd ? "var(--landing-text)" : "var(--landing-text-muted)" }}>
                    {line}
                  </div>
                );
              })}
              <div className="cursor-blink" style={{ color: "var(--landing-primary)" }}> </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );

  const renderTrust = () => enabled.trust !== false && (
    <section className="section py-12" data-testid="landing-trust">
      <p className="text-center text-sm font-mono" style={{ color: "var(--landing-text-muted)" }}>{T(trust.title)}</p>
      <div className="mt-6 flex flex-wrap items-center justify-center gap-x-12 gap-y-4 opacity-60">
        {(trust.logos || []).map((l, i) => l.image_url ? (
          <img key={i} src={l.image_url} alt={l.label} className="h-8 grayscale hover:grayscale-0 transition" />
        ) : (
          <span key={i} className="font-mono text-lg" style={{ color: "var(--landing-text-muted)" }}>{l.label}</span>
        ))}
      </div>
    </section>
  );

  const renderFeatures = () => enabled.features !== false && (
    <section className="section" data-testid="landing-features">
      <div className="section-eyebrow">{T(features.eyebrow)}</div>
      <h2 className="section-title">{T(features.title)}</h2>
      <p className="section-subtitle">{T(features.subtitle)}</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-12">
        {(features.items || []).map((f, i) => {
          const Icon = ICON_MAP[f.icon] || Bot;
          return (
            <div key={i} className="card" data-testid={`feature-${i}`}>
              <div className="icon-pill mb-4"><Icon className="w-5 h-5" /></div>
              <h3 className="text-lg font-semibold mb-2">{T(f.title)}</h3>
              <p className="text-sm" style={{ color: "var(--landing-text-muted)" }}>{T(f.desc)}</p>
            </div>
          );
        })}
      </div>
    </section>
  );

  const renderDemo = () => enabled.demo !== false && (demo.video_url || demo.image_url) && (
    <section className="section" data-testid="landing-demo">
      <div className="section-eyebrow">{T(demo.eyebrow)}</div>
      <h2 className="section-title">{T(demo.title)}</h2>
      <p className="section-subtitle">{T(demo.subtitle)}</p>
      <div className="mt-10 rounded-2xl overflow-hidden border" style={{ borderColor: "var(--landing-border)", aspectRatio: "16/9" }}>
        {demo.video_url ? (
          demo.video_url.includes("youtube") || demo.video_url.includes("youtu.be") ? (
            <iframe className="w-full h-full" src={demo.video_url} title="demo" frameBorder="0" allowFullScreen />
          ) : (
            <video className="w-full h-full" src={demo.video_url} controls />
          )
        ) : (
          <img src={demo.image_url} alt="demo" className="w-full h-full object-cover" />
        )}
      </div>
    </section>
  );

  const renderHow = () => enabled.how_it_works !== false && (
    <section className="section" data-testid="landing-how">
      <div className="section-eyebrow">{T(how.eyebrow)}</div>
      <h2 className="section-title">{T(how.title)}</h2>
      <div className="grid md:grid-cols-3 gap-4 mt-12">
        {(how.steps || []).map((s, i) => (
          <div key={i} className="card" data-testid={`step-${i}`}>
            <div className="font-mono text-3xl font-bold" style={{ color: "var(--landing-primary)" }}>{s.number}</div>
            <h3 className="text-lg font-semibold mt-3">{T(s.title)}</h3>
            <p className="text-sm mt-2" style={{ color: "var(--landing-text-muted)" }}>{T(s.desc)}</p>
          </div>
        ))}
      </div>
    </section>
  );

  const renderPlans = () => enabled.plans !== false && plans.length > 0 && (
    <section className="section" data-testid="landing-plans">
      <div className="text-center">
        <div className="section-eyebrow">{T(plansSection.eyebrow)}</div>
        <h2 className="section-title">{T(plansSection.title)}</h2>
        <p className="section-subtitle mx-auto">{T(plansSection.subtitle)}</p>
        {plansSection.show_billing_toggle !== false && (
          <div className="mt-8 inline-block">
            <div className="billing-toggle">
              {(plansSection.billing_periods || ["monthly", "yearly"]).map((p) => (
                <button key={p} onClick={() => setBilling(p)} className={billing === p ? "active" : ""} data-testid={`billing-${p}`}>
                  {p === "monthly" ? (lang === "es" ? "Mensual" : "Monthly") : (lang === "es" ? "Anual" : "Yearly")}
                </button>
              ))}
            </div>
            {billing === "yearly" && plansSection.yearly_discount_label && (
              <p className="mt-3 text-xs font-mono" style={{ color: "var(--landing-primary)" }}>
                {T(plansSection.yearly_discount_label)}
              </p>
            )}
          </div>
        )}
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-12 items-stretch">
        {plans.map((plan) => {
          const b = plan.billing?.[billing] || plan.billing?.monthly || {};
          const features = plan.features || [];
          return (
            <div key={plan.id} className={`plan-card ${plan.highlighted ? "featured" : ""}`} data-testid={`plan-${plan.slug}`}>
              {plan.highlighted && <div className="plan-badge">{lang === "es" ? "Más popular" : "Most popular"}</div>}
              <div>
                <div className="font-mono text-sm" style={{ color: plan.color || "var(--landing-primary)" }}>{T(plan.name)}</div>
                <p className="text-xs mt-1" style={{ color: "var(--landing-text-muted)" }}>{T(plan.tagline)}</p>
              </div>
              <div className="flex items-baseline gap-1">
                <span className="text-4xl font-bold">${b.price ?? 0}</span>
                <span className="text-sm" style={{ color: "var(--landing-text-muted)" }}>{T(b.period_label)}</span>
              </div>
              <ul className="space-y-2 flex-1">
                {features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm" style={{ color: f.included ? "var(--landing-text)" : "var(--landing-text-muted)" }}>
                    {f.included
                      ? <Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "var(--landing-primary)" }} />
                      : <X className="w-4 h-4 mt-0.5 shrink-0 opacity-40" />}
                    <span className={f.included ? "" : "line-through opacity-60"}>{T(f.label)}</span>
                  </li>
                ))}
              </ul>
              <button onClick={() => openPlanModal(plan)} className={plan.highlighted ? "btn-primary justify-center" : "btn-ghost justify-center"} data-testid={`plan-cta-${plan.slug}`}>
                {T(plan.cta_label)} <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          );
        })}
      </div>
    </section>
  );

  const renderTestimonials = () => enabled.testimonials !== false && (testimonials.items || []).length > 0 && (
    <section className="section" data-testid="landing-testimonials">
      <div className="section-eyebrow">{T(testimonials.eyebrow)}</div>
      <h2 className="section-title">{T(testimonials.title)}</h2>
      <div className="grid md:grid-cols-3 gap-4 mt-12">
        {testimonials.items.map((t, i) => (
          <div key={i} className="card" data-testid={`testimonial-${i}`}>
            <p className="text-sm leading-relaxed">"{T(t.quote)}"</p>
            <div className="mt-5 flex items-center gap-3">
              {t.avatar_url
                ? <img src={t.avatar_url} alt={t.name} className="w-10 h-10 rounded-full object-cover" />
                : <div className="w-10 h-10 rounded-full" style={{ background: "rgba(34,255,157,0.15)" }} />}
              <div>
                <p className="text-sm font-medium">{t.name}</p>
                <p className="text-xs" style={{ color: "var(--landing-text-muted)" }}>{T(t.role)}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );

  const renderFaq = () => enabled.faq !== false && (faq.items || []).length > 0 && (
    <section className="section" data-testid="landing-faq">
      <div className="section-eyebrow">{T(faq.eyebrow)}</div>
      <h2 className="section-title">{T(faq.title)}</h2>
      <div className="mt-8 max-w-3xl">
        {faq.items.map((it, i) => (
          <details key={i} className="faq-item" data-testid={`faq-${i}`}>
            <summary>{T(it.q)}</summary>
            <p>{T(it.a)}</p>
          </details>
        ))}
      </div>
    </section>
  );

  const renderContact = () => enabled.contact !== false && (
    <section className="section" data-testid="landing-contact">
      <div className="card text-center" style={{ padding: 48 }}>
        <div className="section-eyebrow">{T(contact.eyebrow)}</div>
        <h2 className="section-title">{T(contact.title)}</h2>
        <p className="section-subtitle mx-auto mb-8">{T(contact.subtitle)}</p>
        {contact.whatsapp_phone && (
          <a
            href={`https://wa.me/${(contact.whatsapp_phone || "").replace(/\D/g, "")}?text=${encodeURIComponent(T(contact.whatsapp_message))}`}
            target="_blank" rel="noopener noreferrer"
            className="btn-primary"
            data-testid="contact-whatsapp-btn"
          >
            <MessageSquare className="w-4 h-4" /> {T(contact.cta_label)}
          </a>
        )}
      </div>
    </section>
  );

  const renderFooter = () => enabled.footer !== false && (
    <footer className="section py-12 border-t" style={{ borderColor: "var(--landing-border)" }} data-testid="landing-footer">
      <div className="grid md:grid-cols-3 gap-8 items-start">
        <div>
          <div className="font-mono text-lg font-bold" style={{ color: "var(--landing-primary)" }}>
            {settings.brand_name || "Bo-Tito"}
          </div>
          <p className="text-sm mt-2" style={{ color: "var(--landing-text-muted)" }}>{T(footer.tagline)}</p>
        </div>
        <div className="flex gap-4 md:justify-center">
          {footer.social?.instagram && <a href={footer.social.instagram} target="_blank" rel="noreferrer"><Instagram className="w-5 h-5" /></a>}
          {footer.social?.twitter && <a href={footer.social.twitter} target="_blank" rel="noreferrer"><Twitter className="w-5 h-5" /></a>}
          {footer.social?.youtube && <a href={footer.social.youtube} target="_blank" rel="noreferrer"><Youtube className="w-5 h-5" /></a>}
          {footer.social?.github && <a href={footer.social.github} target="_blank" rel="noreferrer"><Github className="w-5 h-5" /></a>}
        </div>
        <div className="md:text-right">
          <div className="flex gap-4 md:justify-end text-sm" style={{ color: "var(--landing-text-muted)" }}>
            {(footer.legal_links || []).map((l, i) => (
              <a key={i} href={l.url} className="hover:underline">{T(l.label)}</a>
            ))}
          </div>
          <p className="text-xs mt-3" style={{ color: "var(--landing-text-muted)" }}>{T(footer.copyright)}</p>
        </div>
      </div>
    </footer>
  );

  const SECTION_RENDERERS = {
    hero: renderHero, trust: renderTrust, features: renderFeatures, demo: renderDemo,
    how_it_works: renderHow, plans: renderPlans, testimonials: renderTestimonials,
    faq: renderFaq, contact: renderContact, footer: renderFooter,
  };

  return (
    <div className="landing-root" data-testid="landing-page">
      {/* NAV */}
      <nav className="flex items-center justify-between px-6 py-5 max-w-7xl mx-auto">
        <div className="flex items-center gap-2">
          {settings.logo_url
            ? <img src={settings.logo_url} alt={settings.brand_name} className="w-8 h-8 rounded" />
            : <div className="w-8 h-8 rounded flex items-center justify-center" style={{ background: "rgba(34,255,157,0.1)", border: "1px solid var(--landing-primary)" }}>
                <span className="font-mono font-bold text-sm" style={{ color: "var(--landing-primary)" }}>B</span>
              </div>}
          <span className="font-mono text-base font-bold">{settings.brand_name}</span>
          <span className="ml-2 text-xs font-mono" style={{ color: "var(--landing-text-muted)" }}>{T(settings.brand_tagline)}</span>
        </div>
        <div className="flex items-center gap-3">
          {(settings.languages_enabled || ["es", "en"]).length > 1 && (
            <div className="lang-switch">
              {(settings.languages_enabled || ["es", "en"]).map((l) => (
                <button key={l} onClick={() => onSwitchLang(l)} className={lang === l ? "active" : ""} data-testid={`lang-${l}`}>{l.toUpperCase()}</button>
              ))}
            </div>
          )}
          <button onClick={() => navigate("/login")} className="btn-ghost text-sm" style={{ padding: "8px 16px" }} data-testid="nav-login">
            {lang === "es" ? "Iniciar sesión" : "Sign in"}
          </button>
          <button onClick={() => navigate("/register")} className="btn-primary text-sm" style={{ padding: "8px 16px" }} data-testid="nav-register">
            {lang === "es" ? "Empezar" : "Start"}
          </button>
        </div>
      </nav>

      {order.map((key) => {
        const render = SECTION_RENDERERS[key];
        return render ? <React.Fragment key={key}>{render()}</React.Fragment> : null;
      })}

      {/* Plan request modal — opens when a CTA on a plan is clicked */}
      {planModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(7,11,20,0.85)", backdropFilter: "blur(8px)" }}
             onClick={() => !submitting && setPlanModal(null)} data-testid="plan-request-modal">
          <div className="terminal-card w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="terminal-bar">
              <span className="terminal-dot" style={{ background: "#ff5f56" }}></span>
              <span className="terminal-dot" style={{ background: "#ffbd2e" }}></span>
              <span className="terminal-dot" style={{ background: "#27c93f" }}></span>
              <span className="ml-3 font-mono text-xs" style={{ color: "var(--landing-text-muted)" }}>
                ./contratar --plan={planModal.slug}
              </span>
            </div>
            {submitted ? (
              <div className="p-7 text-center space-y-4">
                <div className="w-14 h-14 rounded-full mx-auto flex items-center justify-center" style={{ background: "rgba(34,255,157,0.15)" }}>
                  <Check className="w-7 h-7" style={{ color: "var(--landing-primary)" }} />
                </div>
                <h3 className="text-xl font-semibold">{lang === "es" ? "¡Recibido!" : "Got it!"}</h3>
                <p className="text-sm" style={{ color: "var(--landing-text-muted)" }}>
                  {lang === "es"
                    ? "Te mandamos un email con los datos para hacer la transferencia. Apenas confirmemos el pago, activamos tu plan."
                    : "We sent you an email with the bank transfer details. As soon as we confirm payment, your plan is active."}
                </p>
                <button onClick={() => setPlanModal(null)} className="btn-ghost">{lang === "es" ? "Cerrar" : "Close"}</button>
              </div>
            ) : (
              <div className="p-6 space-y-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <span style={{ color: planModal.color || "var(--landing-primary)" }}>{T(planModal.name)}</span>
                  <span className="text-xs font-mono" style={{ color: "var(--landing-text-muted)" }}>
                    · ${planModal.billing?.[billing]?.price ?? 0}{T(planModal.billing?.[billing]?.period_label)}
                  </span>
                </h3>
                <div className="rounded-md p-3 text-xs flex items-center justify-between gap-3"
                     style={{ background: "rgba(34,255,157,0.08)", border: "1px solid rgba(34,255,157,0.25)" }}>
                  <span style={{ color: "var(--landing-text-muted)" }}>
                    {lang === "es" ? "¿Preferís crear cuenta primero?" : "Want to create an account first?"}
                  </span>
                  <Link to={`/register?plan=${encodeURIComponent(planModal.slug)}`}
                        className="font-mono whitespace-nowrap" style={{ color: "var(--landing-primary)" }}
                        data-testid="plan-modal-register-link">
                    {lang === "es" ? "Registrarme →" : "Sign up →"}
                  </Link>
                </div>
                <div className="space-y-3">
                  <div>
                    <label className="text-xs font-mono block mb-1" style={{ color: "var(--landing-text-muted)" }}>{lang === "es" ? "Nombre completo" : "Full name"}</label>
                    <input value={planForm.name} onChange={(e) => setPlanForm({ ...planForm, name: e.target.value })}
                           className="w-full bg-black/40 border border-white/10 rounded px-3 py-2 text-sm text-white" data-testid="plan-form-name" />
                  </div>
                  <div>
                    <label className="text-xs font-mono block mb-1" style={{ color: "var(--landing-text-muted)" }}>Email</label>
                    <input type="email" value={planForm.email} onChange={(e) => setPlanForm({ ...planForm, email: e.target.value })}
                           className="w-full bg-black/40 border border-white/10 rounded px-3 py-2 text-sm text-white" data-testid="plan-form-email" />
                  </div>
                  <div>
                    <label className="text-xs font-mono block mb-1" style={{ color: "var(--landing-text-muted)" }}>WhatsApp{lang === "es" ? " (opcional)" : " (optional)"}</label>
                    <input value={planForm.phone} onChange={(e) => setPlanForm({ ...planForm, phone: e.target.value })}
                           placeholder="+54 9 ..."
                           className="w-full bg-black/40 border border-white/10 rounded px-3 py-2 text-sm text-white" data-testid="plan-form-phone" />
                  </div>
                  <div>
                    <label className="text-xs font-mono block mb-1" style={{ color: "var(--landing-text-muted)" }}>{lang === "es" ? "Método de pago" : "Payment method"}</label>
                    <select value={planForm.payment_method} onChange={(e) => setPlanForm({ ...planForm, payment_method: e.target.value })}
                            className="w-full bg-black/40 border border-white/10 rounded px-3 py-2 text-sm text-white" data-testid="plan-form-payment">
                      <option>Transferencia bancaria</option>
                      <option>Mercado Pago</option>
                      <option>Stripe / Tarjeta</option>
                      <option>{lang === "es" ? "Otro (te contacto)" : "Other (contact me)"}</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-mono block mb-1" style={{ color: "var(--landing-text-muted)" }}>{lang === "es" ? "Notas (opcional)" : "Notes (optional)"}</label>
                    <textarea rows={2} value={planForm.notes} onChange={(e) => setPlanForm({ ...planForm, notes: e.target.value })}
                              className="w-full bg-black/40 border border-white/10 rounded px-3 py-2 text-sm text-white" data-testid="plan-form-notes" />
                  </div>
                  {submitError && <p className="text-xs text-red-400">⚠️ {submitError}</p>}
                </div>
                <div className="flex gap-2 pt-2">
                  <button onClick={() => setPlanModal(null)} disabled={submitting} className="btn-ghost flex-1 justify-center">{lang === "es" ? "Cancelar" : "Cancel"}</button>
                  <button onClick={submitPlan} disabled={submitting} className="btn-primary flex-1 justify-center" data-testid="plan-form-submit">
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ChevronRight className="w-4 h-4" />}
                    {lang === "es" ? "Confirmar" : "Confirm"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
